import { triggerReloginForResponse } from './auth-relogin.ts';

export interface UiChatReloginCopy {
  error: string;
  remediation: string;
}

const DEFAULT_RELOGIN_COPY: UiChatReloginCopy = {
  error: 'Přihlášení vypršelo; obnovuji autorizaci.',
  remediation: 'Po obnovení autorizace budete vráceni na tuto stránku.',
};

/** Narrow same-origin presentation capability for the authenticated SSE chat. */
export async function openUiChatStream(
  payload: Record<string, unknown>,
  signal?: AbortSignal,
  reloginCopy: UiChatReloginCopy = DEFAULT_RELOGIN_COPY,
): Promise<Response> {
  const response = await fetch('/ui/capabilities/chat-stream', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (triggerReloginForResponse(response)) {
    return Response.json({
      error: reloginCopy.error,
      remediation: reloginCopy.remediation,
    }, { status: 401 });
  }
  return response;
}

export interface UiChatTurnHandle {
  turnId: string;
  conversationId: string;
  /** 'running' = tah běží hned; 'queued' = zařazen za běžící tah (position = kolikátý). */
  status: 'running' | 'queued';
  position: number;
  /**
   * SSE eventů tahu jako Response (stejný formát `data: {...}` jako /stream),
   * takže stávající parser zůstává. Po výpadku spojení se helper sám znovu
   * připojí od posledního pořadového čísla — tah na serveru běží dál.
   */
  response: Response;
}

/**
 * Tah chatu na serveru (Filip 2026-09-06): server drží přepis s tool cally,
 * smyčka běží nezávisle na spojení klienta. Klient jen založí tah a čte eventy.
 * `signal` zavírá pouze čtení eventů (odchod z obrazovky), NE tah — ten se
 * zastavuje výhradně přes `interruptUiChatTurn` (odeslat 2×).
 */
export async function openUiChatTurn(
  payload: Record<string, unknown>,
  signal?: AbortSignal,
  reloginCopy: UiChatReloginCopy = DEFAULT_RELOGIN_COPY,
): Promise<UiChatTurnHandle | Response> {
  const created = await fetch('/ui/capabilities/chat-turns', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (triggerReloginForResponse(created)) {
    return Response.json({
      error: reloginCopy.error,
      remediation: reloginCopy.remediation,
    }, { status: 401 });
  }
  if (!created.ok) return created;
  const info = await created.json() as {
    turn_id: string;
    conversation_id: string;
    status: 'running' | 'queued';
    position: number;
  };
  return {
    turnId: info.turn_id,
    conversationId: info.conversation_id,
    status: info.status,
    position: info.position,
    response: openUiChatTurnEvents(info.turn_id, 0, signal),
  };
}

/** „Odeslat 2×": přeruší běžící tah konverzace, zařazený tah jde jako první. */
export async function interruptUiChatTurn(turnId: string): Promise<boolean> {
  const r = await fetch(`/ui/capabilities/chat-turns/${encodeURIComponent(turnId)}/interrupt`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  return r.ok;
}

const RECONNECT_DELAYS_MS = [500, 1000, 2000, 4000, 8000];

/**
 * Eventy tahu od `since` jako SSE Response s automatickým znovupřipojením.
 * Konec = server pošle `turn_end` (nebo [DONE]); do té doby výpadek spojení
 * znamená jen re-attach od posledního seq. Heartbeaty se nepropouští.
 */
export function openUiChatTurnEvents(
  turnId: string,
  since = 0,
  signal?: AbortSignal,
): Response {
  const enc = new TextEncoder();
  const dec = new TextDecoder();
  let last = since;
  let attempt = 0;
  const stream = new ReadableStream<Uint8Array>({
    async start(controller) {
      const push = (line: string) => controller.enqueue(enc.encode(`data: ${line}\n\n`));
      let ended = false;
      while (!ended && !signal?.aborted) {
        let r: Response;
        try {
          r = await fetch(
            `/ui/capabilities/chat-turns/${encodeURIComponent(turnId)}/events?since=${last}`,
            { credentials: 'include', signal },
          );
        } catch {
          if (signal?.aborted) break;
          await new Promise((res) => setTimeout(res, RECONNECT_DELAYS_MS[Math.min(attempt++, RECONNECT_DELAYS_MS.length - 1)]));
          continue;
        }
        if (r.status === 404) {
          push(JSON.stringify({ type: 'error', message: 'Tah na serveru neexistuje.' }));
          break;
        }
        if (!r.ok || !r.body) {
          if (r.status === 401 || r.status === 403) {
            push(JSON.stringify({ type: 'error', message: `Eventy tahu: HTTP ${r.status}` }));
            break;
          }
          await new Promise((res) => setTimeout(res, RECONNECT_DELAYS_MS[Math.min(attempt++, RECONNECT_DELAYS_MS.length - 1)]));
          continue;
        }
        attempt = 0;
        const reader = r.body.getReader();
        let buf = '';
        try {
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += dec.decode(value, { stream: true });
            const lines = buf.split(/\n/);
            buf = lines.pop() ?? '';
            for (const line of lines) {
              if (!line.startsWith('data:')) continue;
              const data = line.slice(5).trim();
              if (!data) continue;
              if (data === '[DONE]') continue; // konec rozhoduje turn_end
              let evt: { type?: string; seq?: number; status?: string };
              try {
                evt = JSON.parse(data) as { type?: string; seq?: number; status?: string };
              } catch {
                continue;
              }
              if (evt.type === 'heartbeat') continue;
              if (typeof evt.seq === 'number') last = Math.max(last, evt.seq);
              if (evt.type === 'turn_end') {
                // Older servers emit EOF immediately for queued turns. Reattach
                // until the server reports a real terminal state, including
                // when the queue lasts longer than the current HTTP connection.
                if (evt.status === 'queued' || evt.status === 'running') continue;
                ended = true;
                push(data);
                break;
              }
              push(data);
            }
            if (ended) break;
          }
        } catch {
          // spojení spadlo uprostřed — smyčka nahoře se připojí znovu od `last`
        }
        if (!ended && !signal?.aborted) {
          await new Promise((res) => setTimeout(res, RECONNECT_DELAYS_MS[Math.min(attempt++, RECONNECT_DELAYS_MS.length - 1)]));
        }
      }
      try {
        controller.enqueue(enc.encode('data: [DONE]\n\n'));
      } catch { /* */ }
      try {
        controller.close();
      } catch { /* */ }
    },
  });
  return new Response(stream, {
    status: 200,
    headers: { 'Content-Type': 'text/event-stream', 'X-Chat-Turn-Id': turnId },
  });
}

/** Preserve structured ACMEngine/API feedback instead of reducing it to HTTP NNN. */
export async function uiChatFailureMessage(response: Response): Promise<string> {
  const fallback = `HTTP ${response.status}`;
  let text = '';
  try {
    text = await response.text();
  } catch {
    return fallback;
  }
  if (!text.trim()) return fallback;
  try {
    const payload = JSON.parse(text) as {
      error?: unknown;
      detail?: unknown;
      remediation?: unknown;
    };
    const parts = [payload.error, payload.detail, payload.remediation]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    return parts.length > 0 ? parts.join(' — ') : fallback;
  } catch {
    return text.trim().slice(0, 500) || fallback;
  }
}
